"use client";

import {
  ApproximatePercentile,
  FreeNightMatch,
  HotelSummary,
  SearchSessionStatus,
  SearchSessionV2,
} from "@/generated/search.openapi";
import { createRoot } from "react-dom/client";
import { FilterOption, HotelData } from "../context/searchProvider";
import HotelMarker from "./mapViewComponents/HotelMarker";
import ClusterMarker from "./mapViewComponents/ClusterMarker";
import CuratedHotelMarker from "./mapViewComponents/CuratedHotelMarker";
import HotelCard from "@/components/section/card/HotelCard";
import { getDisplayNightlyRate, getDisplayTotalCost } from "@/lib/utils";
import React from "react";

// Markers + popovers render into detached createRoot trees (Google Maps owns
// the DOM), so they can't read SearchContext via useSearch(). Resolve the
// filter-aware nightly rate at the caller and thread it through. See GON-5654.
const resolveDisplayNightlyRate = (
  hotel: HotelData,
  filters?: FilterOption
): number | undefined =>
  getDisplayNightlyRate(
    hotel,
    filters?.isRefundable,
    filters?.roomType,
    filters?.bedType
  );

export const LOCATION_PIN_Z_INDEX = 35;
export const INFO_WINDOW_Z_INDEX = 30;
export const HOVER_MARKER_Z_INDEX = 15;
export const ACTIVE_MARKER_Z_INDEX = 20;
export const MARKER_Z_INDEX = 10;
export const CLUSTER_MARKER_Z_INDEX = 5;
export const UNAVAILABLE_MARKER_Z_INDEX = 1;

export type MapElementMarker = {
  marker: google.maps.Marker;
  dimmedMarker?: google.maps.Marker;
  hover: google.maps.Marker;
  zIndex: number;
};

export type MapElements = {
  infoWindow: google.maps.InfoWindow;
};

export type GetHotelMapElements = (
  hotelSummary: HotelSummary,
  searchResponse: SearchSessionV2,
  filters?: FilterOption
) => MapElements;

export const getHotelInfoBox = (
  hotel: HotelSummary,
  searchResponse: SearchSessionV2,
  filters?: FilterOption,
  freeNightMatch?: FreeNightMatch,
  contentFirst?: boolean,
  favoriteState?: boolean,
  favoritePending = false,
  onFavoriteToggle?: () => void
) => {
  const infoWindow = new google.maps.InfoWindow({
    content: "",
    zIndex: INFO_WINDOW_Z_INDEX,
    pixelOffset: new google.maps.Size(0, -5),
  });
  const div = document.createElement("div");
  const root = createRoot(div);

  // GON-6020 content-first: mirror the list card's rate-loading affordance so a
  // rate-less property shows "Checking rates…" / "No rates" in the popover
  // instead of the legacy "sold out". Computed LIVE (not frozen at open) so an
  // already-open popover updates as the hotel's rate merges or the session
  // completes — see `__rerenderRateState` below. Uses the same display-rate
  // logic the card does, and session status for the complete vs loading split.
  const computeAffordance = (
    h: HotelSummary,
    complete: boolean
  ): "loading" | "unavailable" | undefined => {
    if (!contentFirst) return undefined;
    // GON-6088: a present total counts as a rate too (some bookable rates carry a
    // total but no per-night value); the card renders the total in that case.
    const hasRate = Boolean(
      resolveDisplayNightlyRate(h, filters) ||
        getDisplayTotalCost(
          h,
          filters?.isRefundable,
          filters?.roomType,
          filters?.bedType
        ) ||
        h.lowestNightlyPoints
    );
    if (hasRate) return undefined;
    return complete ? "unavailable" : "loading";
  };
  // The popover renders into a detached tree, so it can't read live context;
  // track the latest hotel snapshot + completion here and re-render on change.
  let currentHotel = hotel;
  let currentComplete =
    searchResponse?.sessionStatus === SearchSessionStatus.Complete;

  // Renders the popover content with the current freeNightMatch. Used at
  // initial open AND again from HotelMapView when the batched FNC match
  // resolves while the popover is already open — without this re-render,
  // the badge would stay missing until the user closes + reopens
  // (GON-5965 Phase 4a).
  let lastMatch = freeNightMatch;
  let currentFavoriteState = favoriteState;
  let currentFavoritePending = favoritePending;
  const renderContent = (match: FreeNightMatch | undefined) => {
    lastMatch = match;
    root.render(
      <HotelCard
        hotel={currentHotel}
        numberOfAdults={
          searchResponse?.request?.hotels?.searchOccupants?.numberOfAdults
        }
        childrenAges={
          searchResponse?.request?.hotels?.searchOccupants?.childrenAges
        }
        availableTransferPartners={
          searchResponse?.results?.hotels?.availableTransferPartners
        }
        closeCard={() => {
          infoWindow.close();
        }}
        className="w-[330px]"
        isMapView
        filtersOverride={filters}
        noRateAffordance={computeAffordance(currentHotel, currentComplete)}
        freeNightMatch={match}
        favoriteState={currentFavoriteState}
        favoritePending={currentFavoritePending}
        onFavoriteToggle={onFavoriteToggle}
      />
    );
  };
  // Re-render with a fresh hotel snapshot + completion so an open popover tracks
  // rate deltas / session completion (preserves the current FNC match).
  const rerenderRateState = (freshHotel: HotelSummary, complete: boolean) => {
    currentHotel = freshHotel;
    currentComplete = complete;
    renderContent(lastMatch);
  };
  const rerenderFavorite = (
    isFavorite: boolean | undefined,
    pending: boolean
  ) => {
    currentFavoriteState = isFavorite;
    currentFavoritePending = pending;
    renderContent(lastMatch);
  };
  renderContent(freeNightMatch);
  infoWindow.setContent(div);

  // Store root, cleanup, and re-render hooks on infoWindow.
  type InfoWindowWithHooks = google.maps.InfoWindow & {
    __root: ReturnType<typeof createRoot>;
    __cleanup: () => void;
    __rerenderFreeNightMatch: (match: FreeNightMatch | undefined) => void;
    __rerenderRateState: (freshHotel: HotelSummary, complete: boolean) => void;
    __rerenderFavorite: (
      isFavorite: boolean | undefined,
      pending: boolean
    ) => void;
  };
  const iw = infoWindow as InfoWindowWithHooks;
  iw.__root = root;
  iw.__cleanup = () => {
    root.unmount();
  };
  iw.__rerenderFreeNightMatch = renderContent;
  iw.__rerenderRateState = rerenderRateState;
  iw.__rerenderFavorite = rerenderFavorite;

  return infoWindow;
};

export const getHotelMapElements: GetHotelMapElements = (
  hotelSummary,
  searchResponse,
  filters
) => {
  return {
    infoWindow: getHotelInfoBox(hotelSummary, searchResponse, filters),
  };
};

export const getClusterRadius = (count: number) => {
  const minCount = 0;
  const maxCount = 1400;
  const minOutput = 70;
  const maxOutput = 200;
  const proportion = (count - minCount) / (maxCount - minCount);
  return minOutput + proportion * (maxOutput - minOutput);
};

export const setAdvancedCuratedHotelMarker = (
  hotel: HotelData,
  options: google.maps.marker.AdvancedMarkerElementOptions,
  isDimmed?: boolean,
  isSelected?: boolean
) => {
  const div = document.createElement("div");
  const root = createRoot(div);

  root.render(
    <CuratedHotelMarker
      hotel={hotel}
      isDimmed={isDimmed}
      isSelected={isSelected}
    />
  );

  // Unavailable hotels get the lowest z-index
  const isUnavailable = !hotel.nightlyRate;
  const zIndex = isUnavailable
    ? UNAVAILABLE_MARKER_Z_INDEX
    : MARKER_Z_INDEX + (isSelected ? 1 : 0);

  return new google.maps.marker.AdvancedMarkerElement({
    ...options,
    content: div,
    zIndex,
  });
};

export const setAdvancedHotelMarker = (
  hotel: HotelData,
  options: google.maps.marker.AdvancedMarkerElementOptions,
  isHovered?: boolean,
  onHoverChange?: (isHovered: boolean) => void,
  filters?: FilterOption
) => {
  const div = document.createElement("div");
  // Ensure the div can receive pointer events
  div.style.pointerEvents = "auto";
  div.style.cursor = "pointer";

  const root = createRoot(div);
  const displayNightlyRate = resolveDisplayNightlyRate(hotel, filters);
  // Check if hotel is unavailable (no nightly rate)
  const isUnavailable = !hotel.nightlyRate;
  // Unavailable hotels always get the lowest z-index
  // For available hotels: use options.zIndex if provided, otherwise use default + hover bonus
  const zIndex = isUnavailable
    ? UNAVAILABLE_MARKER_Z_INDEX
    : isHovered
      ? HOVER_MARKER_Z_INDEX
      : options.zIndex ?? MARKER_Z_INDEX;

  root.render(
    <HotelMarker
      hotel={hotel}
      isHovered={isHovered}
      zIndex={zIndex}
      displayNightlyRate={displayNightlyRate}
    />
  );

  // Store event listener functions so we can remove them later
  const mouseEnterHandler = () => onHoverChange?.(true);
  const mouseLeaveHandler = () => onHoverChange?.(false);

  // Add hover listeners to the div element
  // Use a small delay to ensure the div is rendered before attaching listeners
  if (onHoverChange) {
    setTimeout(() => {
      div.addEventListener("mouseenter", mouseEnterHandler);
      div.addEventListener("mouseleave", mouseLeaveHandler);
    }, 0);
  }

  const marker = new google.maps.marker.AdvancedMarkerElement({
    ...options,
    content: div,
    zIndex,
  });

  // Store root, hotel, and cleanup functions on marker
  const typedMarker = marker as google.maps.marker.AdvancedMarkerElement & {
    __root: ReturnType<typeof createRoot>;
    __hotel: HotelData;
    __displayNightlyRate: number | undefined;
    __div: HTMLDivElement;
    __isUnmounted: boolean;
    __cleanup: () => void;
  };

  typedMarker.__root = root;
  typedMarker.__hotel = hotel;
  typedMarker.__displayNightlyRate = displayNightlyRate;
  typedMarker.__div = div;
  typedMarker.__isUnmounted = false;
  typedMarker.__cleanup = () => {
    typedMarker.__isUnmounted = true;
    // Remove event listeners
    div.removeEventListener("mouseenter", mouseEnterHandler);
    div.removeEventListener("mouseleave", mouseLeaveHandler);
    // Unmount React root to prevent memory leak
    root.unmount();
  };

  return marker;
};

export const updateHotelMarkerHover = (
  marker: google.maps.marker.AdvancedMarkerElement,
  isHovered: boolean,
  isListCardHovered?: boolean,
  isSelected?: boolean
) => {
  const markerWithData = marker as google.maps.marker.AdvancedMarkerElement & {
    __root?: ReturnType<typeof createRoot>;
    __hotel?: HotelData;
    __displayNightlyRate?: number;
    __isUnmounted?: boolean;
  };

  const root = markerWithData.__root;
  const hotel = markerWithData.__hotel;

  if (root && hotel && !markerWithData.__isUnmounted) {
    // Check if hotel is unavailable (no nightly rate)
    const isUnavailable = !hotel.nightlyRate;
    // Marker shows black if: selected OR hovered (marker or list card)
    const shouldShowHovered = isSelected || isHovered || isListCardHovered;
    const zIndex = isSelected
      ? ACTIVE_MARKER_Z_INDEX
      : shouldShowHovered
        ? HOVER_MARKER_Z_INDEX
        : isUnavailable
          ? UNAVAILABLE_MARKER_Z_INDEX
          : MARKER_Z_INDEX;
    marker.zIndex = zIndex;
    root.render(
      <HotelMarker
        hotel={hotel}
        isHovered={shouldShowHovered}
        zIndex={zIndex}
        displayNightlyRate={markerWithData.__displayNightlyRate}
      />
    );
  }
};

export const setAdvancedClusterMarker = (
  clusterNumber: number,
  options: google.maps.marker.AdvancedMarkerElementOptions,
  isHovered?: boolean
) => {
  const div = document.createElement("div");
  const root = createRoot(div);

  root.render(
    <ClusterMarker clusterNumber={clusterNumber} isHovered={isHovered} />
  );

  const marker = new google.maps.marker.AdvancedMarkerElement({
    ...options,
    content: div,
    zIndex: CLUSTER_MARKER_Z_INDEX,
  });

  const typedMarker = marker as google.maps.marker.AdvancedMarkerElement & {
    __root: ReturnType<typeof createRoot>;
    __clusterNumber: number;
    __isMouseHovered: boolean;
    __isListHovered: boolean;
    __cleanup: () => void;
  };

  const mouseEnterHandler = () => {
    typedMarker.__isMouseHovered = true;
    updateClusterMarkerHover(marker, true);
  };
  const mouseLeaveHandler = () => {
    typedMarker.__isMouseHovered = false;
    // Preserve highlight if a hotel inside this cluster is still list-hovered
    updateClusterMarkerHover(marker, typedMarker.__isListHovered ?? false);
  };

  setTimeout(() => {
    div.addEventListener("mouseenter", mouseEnterHandler);
    div.addEventListener("mouseleave", mouseLeaveHandler);
  }, 0);

  typedMarker.__root = root;
  typedMarker.__clusterNumber = clusterNumber;
  typedMarker.__isMouseHovered = false;
  typedMarker.__isListHovered = false;
  typedMarker.__cleanup = () => {
    div.removeEventListener("mouseenter", mouseEnterHandler);
    div.removeEventListener("mouseleave", mouseLeaveHandler);
    root.unmount();
  };

  (marker as unknown as HTMLElement).style.zIndex = String(
    CLUSTER_MARKER_Z_INDEX
  );

  return marker;
};

export const updateClusterMarkerHover = (
  marker: google.maps.marker.AdvancedMarkerElement,
  isHovered: boolean
) => {
  const typedMarker = marker as google.maps.marker.AdvancedMarkerElement & {
    __root?: ReturnType<typeof createRoot>;
    __clusterNumber?: number;
  };

  const root = typedMarker.__root;
  const clusterNumber = typedMarker.__clusterNumber;

  if (root && clusterNumber !== undefined) {
    const zIndex = isHovered ? HOVER_MARKER_Z_INDEX : CLUSTER_MARKER_Z_INDEX;
    marker.zIndex = zIndex;
    // AdvancedMarkerElement extends HTMLElement — set CSS z-index directly on
    // the marker node since marker.zIndex alone may not update the DOM stacking.
    (marker as unknown as HTMLElement).style.zIndex = String(zIndex);
    root.render(
      <ClusterMarker clusterNumber={clusterNumber} isHovered={isHovered} />
    );
  }
};

export const getApproximatePercentile = (
  hotel: HotelData,
  isPoints?: boolean
) => {
  const dealScore = isPoints ? hotel.pointsDealScore : hotel.cashDealScore;
  if (dealScore === undefined) return undefined;
  switch (true) {
    case 0 < dealScore && dealScore <= 3.33:
      return ApproximatePercentile.Low;
    case 3.33 < dealScore && dealScore <= 6.66:
      return ApproximatePercentile.Average;
    case 6.66 < dealScore && dealScore <= 10:
      return ApproximatePercentile.High;
  }
};

export const getPercentileNumber = (hotel: HotelData, isPoints?: boolean) => {
  return isPoints
    ? hotel.pointsDealScore?.toFixed(1)
    : hotel.cashDealScore?.toFixed(1);
};
